import React from 'react';
import { Box, Text, SimpleGrid, Tooltip, Wrap, WrapItem, Tag } from '@chakra-ui/react';
import { HeatMapData } from '../types';
import { useLanguage } from '../hooks/useLanguage';
import { getRiskLevel, LEVEL_COLORS } from '../utils/riskLevel';

interface HeatMapProps {
  data: HeatMapData;
  matrixKey?: 'inherent' | 'residual';
  onRiskCodeClick?: (riskCode: string) => void;
}

const HeatMap: React.FC<HeatMapProps> = ({ data, matrixKey = 'inherent', onRiskCodeClick }) => {
  const { t } = useLanguage();
  const source = matrixKey === 'residual' ? data.residual_matrix : data.inherent_matrix;
  const showCodes = data.show_codes ?? false;

  const renderCell = (cell: number | string[], likelihood: number, impact: number) => {
    const level = getRiskLevel(likelihood, impact);
    const bgColor = LEVEL_COLORS[level].bg;
    const textColor = LEVEL_COLORS[level].text;

    if (showCodes && Array.isArray(cell)) {
      const codes = cell as string[];
      return (
        <Tooltip
          key={`${likelihood}-${impact}`}
          label={`${t(`level.${level}`)} — ${t('common.items', { n: codes.length })}`}
          placement="top"
        >
          <Box
            p={1}
            bg={bgColor}
            color={textColor}
            borderRadius="md"
            textAlign="center"
            minH="56px"
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="center"
            gap={1}
          >
            {codes.length > 0 ? (
              <Wrap spacing={0} justify="center">
                {codes.map((code) => (
                  <WrapItem key={code}>
                    <Tag
                      size="sm"
                      bg={textColor}
                      color={bgColor}
                      cursor="pointer"
                      _hover={{ opacity: 0.8, transform: 'scale(1.05)' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRiskCodeClick?.(code);
                      }}
                      fontWeight="bold"
                      fontSize="10px"
                      minH="20px"
                    >
                      {code}
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            ) : (
              <Text fontWeight="bold" fontSize="lg">0</Text>
            )}
          </Box>
        </Tooltip>
      );
    }

    const count = (cell as number) || 0;
    return (
      <Tooltip
        key={`${likelihood}-${impact}`}
        label={`${t(`level.${level}`)} — ${t('common.items', { n: count })}`}
        placement="top"
      >
        <Box
          p={3}
          bg={bgColor}
          color={textColor}
          borderRadius="md"
          textAlign="center"
          _hover={{ opacity: 0.8 }}
          minH="56px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text fontWeight="bold" fontSize="lg">{count}</Text>
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box>
      {/* Y-axis label */}
      <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
        {t('heatmap.impact')}
      </Text>

      {/* Data rows: impact 5 (top) to 1 (bottom) */}
      {[5, 4, 3, 2, 1].map((impact) => (
        <SimpleGrid key={impact} columns={6} spacing={1} mb={1}>
          <Box display="flex" alignItems="center" justifyContent="center" p={2} bg="gray.100" borderRadius="md">
            <Text fontSize="sm" fontWeight="bold">{impact}</Text>
          </Box>
          {[1, 2, 3, 4, 5].map((likelihood) => {
            const cell = source?.[likelihood - 1]?.[impact - 1] ?? (showCodes ? [] : 0);
            return renderCell(cell, likelihood, impact);
          })}
        </SimpleGrid>
      ))}

      {/* Header row: 1-5 (bottom) */}
      <SimpleGrid columns={6} spacing={1} mt={1}>
        <Box></Box>
        {[1, 2, 3, 4, 5].map((l) => (
          <Box key={l} textAlign="center" p={2} bg="gray.100" borderRadius="md">
            <Text fontSize="sm" fontWeight="bold">{l}</Text>
          </Box>
        ))}
      </SimpleGrid>

      {/* X-axis label */}
      <Box textAlign="center" mt={2}>
        <Text fontSize="xs" fontWeight="bold" color="gray.600">
          {t('heatmap.likelihood')}
        </Text>
      </Box>
    </Box>
  );
};

export default HeatMap;
